import { useEffect, useState, lazy, Suspense } from "react";
import Hero from "./Hero";
import About from "./About";
import { LazySection } from "../../../components/common/LazySection";
import "../../styles/animations.css";
import { scrollToSection } from "../../../utils/functions";
import { BubbleField } from "../../../components/decor/BubbleField";
import { DecorZone } from "../../../components/decor/DecorZone";
import { HOME_PAGE_BUBBLES } from "../../../data/decor/bubbles";
import SEO from "../../../components/SEO";
import { ARTICLES_VISIBLE } from "../../../config/features";

const VISIBLE_HOME_PAGE_BUBBLES = HOME_PAGE_BUBBLES.slice(0, 9);

// Lazy-loaded components below the fold
const QuotesSection = lazy(() => import("./QuotesSection"));
const Testimonial = lazy(() => import("./Testimonial"));
const Cta = lazy(() => import("./Cta"));
const Features = lazy(() => import("./Features"));
const Services = lazy(() => import("./Services"));
const Contact = lazy(() => import("./Contact"));
const Faq = lazy(() => import("./Faq"));
const Insurance = lazy(() => import("./Insurance"));
const AboutUs = lazy(() => import("../aboutus/AboutUs"));
const UniversitiesSection = lazy(() => import("../../components/home/universities/UniversitiesSection").then(m => ({ default: m.UniversitiesSection })));
const Team = lazy(() => import("../../../components/aboutus/team/Team"));
const Partners = lazy(() => import("./Partners"));
const SocialFeed = lazy(() => import("./SocialFeed"));
const BlogSection = lazy(() => import("../../components/blog/BlogSection").then(m => ({ default: m.BlogSection })));
const CartoonsSection = lazy(() => import("./CartoonsSection"));

function HomePage() {
  const [animationStarted, setAnimationStarted] = useState(false);

  useEffect(() => {
    const animationTimer = setTimeout(() => {
      setAnimationStarted(true);
    }, 100);

    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.replace("#", "");
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 500);
    }

    return () => {
      clearTimeout(animationTimer);
    };
  }, []);

  return (
    <div className="relative overflow-hidden">
      <SEO
        title="Psychiatric Care & Online ADHD Testing"
        description="ADHD-AID Psychiatry offers specialized online ADHD testing, board-certified diagnosis, and psychiatric medication management. Serving Virginia, Maryland, Florida, Massachusetts, Nevada, and DC."
      />

      <BubbleField
        bubbles={VISIBLE_HOME_PAGE_BUBBLES}
        isStatic={true}
        className="absolute inset-0 w-full min-h-[500vh] z-0"
      />

      <div
        className="absolute inset-0 w-full min-h-[500vh] pointer-events-none z-[1]"
        style={{
          background:
            "linear-gradient(to bottom, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.48) 25%, rgba(255,255,255,0.42) 100%)",
        }}
      />

      <div className="relative z-10">
        <DecorZone zone="heroAbout" bubblesVisible={animationStarted}>
          <Hero />
          <div className="mt-12 sm:mt-16 md:mt-20 lg:mt-24 xl:mt-28 2xl:mt-32">
            <Suspense fallback={<div className="min-h-[200px]" />}>
              <LazySection id="insurance">
                <Insurance />
              </LazySection>
            </Suspense>
          </div>
          <About />
        </DecorZone>

        <Suspense fallback={<div className="min-h-[200px]" />}>
          <DecorZone zone="mid">
            <LazySection id="services">
              <Services />
            </LazySection>
            <LazySection id="features">
              <Features />
            </LazySection>
            <LazySection id="quotes">
              <QuotesSection />
            </LazySection>
            <LazySection id={["states", "peer-support"]}>
              <AboutUs />
            </LazySection>
          </DecorZone>

          <LazySection id="universities">
            <UniversitiesSection />
          </LazySection>

          <DecorZone zone="afterUni">
            <LazySection id="partners">
              <Partners />
            </LazySection>
            <LazySection id="team">
              <Team />
            </LazySection>
            <LazySection id="testimonials">
              <Testimonial />
            </LazySection>
            {ARTICLES_VISIBLE ? (
              <LazySection id="blog">
                <BlogSection />
              </LazySection>
            ) : null}
            <LazySection id="cartoons">
              <CartoonsSection />
            </LazySection>
            <LazySection id="social">
              <SocialFeed />
            </LazySection>
            <LazySection id="cta">
              <Cta />
            </LazySection>
            <div
              style={{
                backgroundImage: `
                  radial-gradient(42rem 34rem at 6% 72%, rgba(147, 197, 253, 0.2) 0%, rgba(147, 197, 253, 0.09) 42%, transparent 76%),
                  radial-gradient(46rem 38rem at 94% 90%, rgba(251, 113, 133, 0.15) 0%, rgba(251, 113, 133, 0.06) 46%, transparent 78%)
                `,
              }}
            >
              <LazySection id="faq">
                <Faq />
              </LazySection>
              <LazySection id="appointment">
                <Contact />
              </LazySection>
            </div>
          </DecorZone>
        </Suspense>
      </div>
    </div>
  );
}

export default HomePage;
